import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { elegirUsuario } from '../store/cuenta';
import { useAcceso } from '../store/acceso';
import { Dialogo } from '../ui/primitivas';
import { ErrorEnLinea } from './plano';

/**
 * EL CÓDIGO DE RECUPERACIÓN, enseñado una sola vez.
 *
 * Sin correo, este código es lo único que deja volver a entrar con la contraseña
 * olvidada. Por eso el diálogo no se cierra hasta marcar «Lo he guardado»: cerrarlo
 * sin querer es perder la llave. Se monta en la raíz, por encima de la puerta y de
 * la app, y el código vive solo en memoria.
 *
 * Las cuentas de antes, que entraban con correo, eligen aquí su usuario primero: el
 * código no sirve de nada sin el usuario con el que se usa.
 */
export function CodigoRecuperacion(): ReactElement | null {
  const { codigo, pedirUsuario, cerrarCodigo } = useAcceso();
  const [guardado, setGuardado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [usuario, setUsuario] = useState('');
  const [usuarioListo, setUsuarioListo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ids = useId();

  if (codigo === null) return null;
  const faltaUsuario = pedirUsuario && usuarioListo === null;

  const cerrar = () => {
    if (faltaUsuario || !guardado) return;
    setGuardado(false);
    setCopiado(false);
    cerrarCodigo();
  };

  const guardarUsuario = async (evento: FormEvent) => {
    evento.preventDefault();
    const problema = await elegirUsuario(usuario);
    if (problema) {
      setError(problema);
      return;
    }
    setError(null);
    setUsuarioListo(usuario.trim());
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
    } catch {
      setCopiado(false);
    }
  };

  const descargar = () => {
    const texto = [
      'selflevel · código de recuperación',
      '',
      codigo,
      '',
      'Con tu usuario y este código puedes poner una contraseña nueva si la olvidas.',
      'Cada código sirve una vez; al usarlo recibes otro.',
    ].join('\n');
    const enlace = document.createElement('a');
    enlace.href = URL.createObjectURL(new Blob([texto], { type: 'text/plain' }));
    enlace.download = 'selflevel-codigo-de-recuperacion.txt';
    enlace.click();
    URL.revokeObjectURL(enlace.href);
  };

  return (
    <Dialogo
      open
      onClose={cerrar}
      titulo={faltaUsuario ? 'Elige tu usuario' : 'Guarda tu código de recuperación'}
    >
      {faltaUsuario ? (
        <form className="stack" onSubmit={(e) => void guardarUsuario(e)}>
          <p className="t-body">
            Desde ahora se entra con usuario. Elige uno; tu correo sigue valiendo para entrar.
          </p>
          <div className="field">
            <label htmlFor={`${ids}-usuario`}>Usuario</label>
            <input
              id={`${ids}-usuario`}
              className="input"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
            />
            <span className="t-sm t-dim">De 3 a 24: letras, números, punto o guion bajo.</span>
          </div>
          {error ? <ErrorEnLinea mensaje={error} /> : null}
          <button className="btn btn--primary btn--block" type="submit">
            Guardar usuario
          </button>
        </form>
      ) : (
        <div className="stack codigo-recuperacion">
          <p className="t-body">
            Si algún día olvidas la contraseña, con tu usuario y este código pones una nueva desde
            cualquier móvil. No se vuelve a enseñar.
          </p>
          <p className="codigo-recuperacion__codigo" aria-label={`Código: ${codigo}`}>
            {codigo}
          </p>
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <button className="btn grow" type="button" onClick={() => void copiar()}>
              {copiado ? 'Copiado' : 'Copiar'}
            </button>
            <button className="btn grow" type="button" onClick={descargar}>
              Descargar
            </button>
          </div>
          <label className="row codigo-recuperacion__check">
            <input
              type="checkbox"
              checked={guardado}
              onChange={(e) => setGuardado(e.target.checked)}
            />
            <span>Lo he guardado en un sitio seguro</span>
          </label>
          <button
            className="btn btn--primary btn--block"
            type="button"
            disabled={!guardado}
            onClick={cerrar}
          >
            Continuar
          </button>
        </div>
      )}
    </Dialogo>
  );
}
